import {
  formatCreditCount,
  getAiUsageDisplay,
} from '@affine/core/components/ai-credits/utils';
import { UserCopilotQuotaService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { cssVar } from '@toeverything/theme';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';

import * as styles from './index.css';

/**
 * yoDEV (YOD-627): the sidebar shows what the user can still spend (free
 * allowance left plus every credit), not a used/limit ratio: with credits the
 * old `limit` was allowance + credits, which read as "3 / 1,120".
 */
export const AIUsage = () => {
  const t = useI18n();
  const copilotQuotaService = useService(UserCopilotQuotaService);

  useEffect(() => {
    copilotQuotaService.copilotQuota.revalidate();
  }, [copilotQuotaService]);

  const quota = useLiveData(copilotQuotaService.copilotQuota.quota$);
  const loadError = useLiveData(copilotQuotaService.copilotQuota.error$);

  const workspaceDialogService = useService(WorkspaceDialogService);

  const goToAccountSetting = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'account',
    });
  }, [workspaceDialogService]);

  if (!quota) {
    if (loadError) console.error(loadError);
    return null;
  }

  const display = getAiUsageDisplay(quota);

  if (display.kind === 'unlimited') {
    return (
      <div
        onClick={goToAccountSetting}
        data-pro
        data-testid="ai-usage-unlimited"
        className={clsx(styles.usageBlock, styles.aiUsageBlock)}
      >
        <div className={styles.usageLabel}>
          <div className={styles.usageLabelTitle}>
            {t['com.affine.user-info.usage.ai']()}
          </div>
        </div>
        <div className={styles.usageLabel}>
          {t['com.affine.ai-credits.usage.unlimited']()}
        </div>
      </div>
    );
  }

  // The bar is this month's free allowance; the label is everything left.
  const percent = Math.max(0.5, display.freePercentUsed);
  const color =
    display.totalRemaining === 0 || (display.credits === 0 && percent > 80)
      ? cssVar('errorColor')
      : cssVar('processingColor');

  return (
    <div
      onClick={goToAccountSetting}
      data-testid="ai-usage-remaining"
      className={clsx(styles.usageBlock, styles.aiUsageBlock)}
      style={assignInlineVars({
        [styles.progressColorVar]: color,
      })}
    >
      <div className={styles.usageLabel}>
        <div>
          <span className={styles.usageLabelTitle}>
            {t['com.affine.user-info.usage.ai']()}
          </span>
          <span>
            {t['com.affine.ai-credits.usage.remaining']({
              remaining: formatCreditCount(display.totalRemaining, t.language),
            })}
          </span>
        </div>
      </div>

      <div className={styles.cloudUsageBar}>
        <div
          className={styles.cloudUsageBarInner}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};
